/**
 * Convite de usuário de empresa (E9, MT-905): as duas rotas públicas que o link do e-mail abre.
 *
 * Nenhuma delas exige sessão — o token é a credencial, e o backend ignora o `Authorization` que o
 * interceptor do `apiClient` manda quando há sessão aberta neste navegador.
 */
import { AxiosError } from 'axios';
import { z } from 'zod';

import { apiClient } from '~/api/client';
import type { components } from '~/types/api';

export type InvitationDetail = components['schemas']['InvitationDetail'];
export type InvitationAcceptRequest = components['schemas']['InvitationAcceptRequest'];
export type AcceptedInvitation = components['schemas']['AcceptedInvitation'];
export type InvitationRole = InvitationDetail['role'];

/** Códigos que o convite devolve (decisão 10 de E9); os demais caem no aviso genérico da tela. */
export const INVITATION_REFUSAL = {
  invalid: 'INVITATION_INVALID',
  expired: 'INVITATION_EXPIRED',
  accepted: 'INVITATION_ACCEPTED',
  emailTaken: 'INVITATION_EMAIL_TAKEN',
  dataInvalid: 'INVITATION_DATA_INVALID',
  officeSuspended: 'OFFICE_SUSPENDED',
} as const;

function invitationUrl(token: string, suffix = ''): string {
  return `/auth/api/invitations/${encodeURIComponent(token)}/${suffix}`;
}

export async function fetchInvitation(token: string): Promise<InvitationDetail> {
  const response = await apiClient.get<InvitationDetail>(invitationUrl(token));
  return response.data;
}

export async function acceptInvitation(
  token: string,
  body: InvitationAcceptRequest,
): Promise<AcceptedInvitation> {
  const response = await apiClient.post<AcceptedInvitation>(invitationUrl(token, 'accept/'), body);
  return response.data;
}

/**
 * A recusa como a tela precisa dela: o código, a mensagem do backend e, em
 * `INVITATION_DATA_INVALID`, o erro por campo que `details.errors` traz.
 *
 * O corpo é lido por schema e não por asserção de tipo: um proxy no caminho pode devolver
 * qualquer coisa, e aí a tela cai no aviso genérico em vez de quebrar.
 */
const refusalSchema = z.object({
  code: z.string(),
  message: z.string().catch(''),
  details: z.object({ errors: z.record(z.array(z.string())).catch({}) }).catch({ errors: {} }),
});

export interface InvitationRefusal {
  code: string;
  message: string;
  /** Campo → primeira mensagem, como o backend as ordenou. */
  fieldErrors: Record<string, string>;
}

function firstMessageByField(errors: Record<string, string[]>): Record<string, string> {
  const byField: Record<string, string> = {};
  for (const [field, messages] of Object.entries(errors)) {
    const [first] = messages;
    if (first) {
      byField[field] = first;
    }
  }
  return byField;
}

export function invitationRefusalOf(error: unknown): InvitationRefusal | null {
  if (!(error instanceof AxiosError)) return null;
  const parsed = refusalSchema.safeParse(error.response?.data);
  if (!parsed.success) return null;
  return {
    code: parsed.data.code,
    message: parsed.data.message,
    fieldErrors: firstMessageByField(parsed.data.details.errors),
  };
}
